import type { FC, ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CheckIcon } from '@heroicons/react/solid';
import { motion } from 'motion/react';
import { useState, useEffect } from 'react';
import Lottie from 'react-lottie';

export type StepProgressStatus = 'completed' | 'current' | 'pending';

export interface StepProgressItem {
  label: string;
  status: StepProgressStatus;
  icon: ComponentType<{ className?: string }>;
  animateCheckmark?: boolean; // Use Lottie animation for checkmark appearance
  lottieAnimationData?: any; // Lottie animation JSON data
  substeps?: {
    label: string;
    status: StepProgressStatus;
  }[];
}

interface StepProgressListProps {
  items: StepProgressItem[];
  showSubsteps?: boolean;
}

/**
 * Displays a vertical progress list showing main steps and optional substeps
 *
 * Features:
 * - Shows step status (completed, current, pending) with color-coded icons
 * - Optional Lottie animation for special celebration (via lottieAnimationData)
 * - Used in InitActivationStep and ReadyToGoStep to show overall progress
 */
const StepProgressList: FC<StepProgressListProps> = ({
  items,
  showSubsteps = false,
}) => {
  const { t } = useTranslation();
  const [animatedItems, setAnimatedItems] = useState<Set<number>>(new Set());

  // Track which items should show animation
  useEffect(() => {
    const newAnimatedItems = new Set<number>();
    items.forEach((item, index) => {
      if (item.animateCheckmark) {
        newAnimatedItems.add(index);
      }
    });
    setAnimatedItems(newAnimatedItems);
  }, [items]);

  return (
    <ul className="flex flex-col gap-6 relative">
      {items.map((item, index) => {
        const isCompleted = item.status === 'completed';
        const isCurrent = item.status === 'current';
        const showConnector = index < items.length - 1;
        const shouldShowLottie =
          animatedItems.has(index) && item.animateCheckmark;

        return (
          <li key={index} className="relative">
            {/* Main step */}
            <div className="flex items-center gap-3 relative">
              <div className="relative flex items-start">
                {/* Icon */}
                {shouldShowLottie && item.lottieAnimationData ? (
                  // Lottie animation for special celebration
                  <motion.div
                    key={`${index}-lottie`}
                    className="w-8 h-8 rounded-full bg-success-base flex items-center justify-center overflow-hidden"
                    initial={{ scale: 0.3, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                      delay: 0.2,
                      type: 'spring',
                      stiffness: 200,
                      damping: 15,
                    }}
                  >
                    <Lottie
                      options={{
                        loop: false,
                        autoplay: true,
                        animationData: item.lottieAnimationData,
                        rendererSettings: {
                          preserveAspectRatio: 'xMidYMid slice',
                        },
                      }}
                      height={32}
                      width={32}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key={
                      item.animateCheckmark
                        ? `${index}-animating`
                        : `${index}-static`
                    }
                    className={classNames(
                      'w-8 h-8 rounded-full flex items-center justify-center',
                      {
                        'bg-success-base': isCompleted,
                        'bg-bank': isCurrent,
                        'bg-gray-200': !isCompleted && !isCurrent,
                      },
                    )}
                  >
                    {isCompleted ? (
                      <CheckIcon className="w-5 h-5 text-white" />
                    ) : (
                      <item.icon
                        className={classNames('w-5 h-5', {
                          'text-white': isCurrent,
                          'text-gray-400': !isCurrent,
                        })}
                      />
                    )}
                  </motion.div>
                )}

                {/* Connector line */}
                {showConnector && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-9 w-[1px] h-4 bg-gray-300" />
                )}
              </div>

              {/* Label */}
              <span
                className={classNames({
                  'text-gray-900 font-semibold': isCurrent,
                  'text-gray-700': isCompleted,
                  'text-gray-500': !isCompleted && !isCurrent,
                })}
              >
                {t(item.label)}
              </span>
            </div>

            {/* Substeps (if enabled and present) */}
            {showSubsteps && item.substeps && item.substeps.length > 0 && (
              <ul className="ml-4 mt-3 space-y-3 border-l-2 border-gray-200 pl-6">
                {item.substeps.map((substep, substepIndex) => {
                  const substepCompleted = substep.status === 'completed';
                  const substepCurrent = substep.status === 'current';

                  return (
                    <li
                      key={substepIndex}
                      className="flex items-center gap-2 text-sm"
                    >
                      {substepCompleted ? (
                        <CheckIcon className="w-4 h-4 text-success-base flex-shrink-0" />
                      ) : substepCurrent ? (
                        <div className="w-4 h-4 rounded-full border-2 border-bank flex-shrink-0" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                      )}
                      <span
                        className={classNames({
                          'text-gray-900 font-medium': substepCurrent,
                          'text-gray-600': substepCompleted,
                          'text-gray-400': !substepCompleted && !substepCurrent,
                        })}
                      >
                        {substep.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default StepProgressList;
